import { useState, useEffect } from 'react';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import SupplierLayout from '../../components/layout/SupplierLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Star,
  Loader2,
  MessageSquare,
  TrendingUp,
  Users,
  Calendar,
  Filter,
  ThumbsUp
} from 'lucide-react';
import { toast } from 'sonner';

const SupplierRatingsPage = () => {
  const { api } = useSupplierAuth();
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [stats, setStats] = useState({
    average: 0,
    count: 0,
    distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const response = await api.get('/ratings');
        setRatings(response.data.ratings || []);
        setStats(response.data.stats || {
          average: 0,
          count: 0,
          distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        });
      } catch (error) {
        toast.error('Kon beoordelingen niet laden');
      } finally {
        setLoading(false);
      }
    };

    fetchRatings();
  }, [api]);

  const filteredRatings = ratings.filter(rating => {
    if (filter === 'all') return true;
    if (filter === 'with_review') return rating.review && rating.review.length > 0;
    return rating.rating === parseInt(filter);
  });

  const StarDisplay = ({ rating, size = 'sm' }) => {
    const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClass} ${
              star <= rating
                ? 'text-yellow-500 fill-yellow-500'
                : 'text-neutral-300 dark:text-neutral-600'
            }`}
            strokeWidth={1.5}
          />
        ))}
      </div>
    );
  };

  const RatingBar = ({ stars, count, total }) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="w-12 text-neutral-500 dark:text-neutral-400">{stars} ster</span>
        <Progress value={percentage} className="flex-1 h-2" />
        <span className="w-8 text-right text-neutral-600 dark:text-neutral-300">{count}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <SupplierLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </SupplierLayout>
    );
  }

  return (
    <SupplierLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
            Feedback
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-black dark:text-white" data-testid="ratings-page-title">
            Beoordelingen
          </h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Bekijk wat klanten over uw dienstverlening zeggen
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-black dark:text-white">
                    {stats.average.toFixed(1)}
                  </p>
                  <p className="text-xs text-neutral-500">Gemiddelde score</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <Star className="h-5 w-5 text-yellow-600 fill-yellow-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-black dark:text-white">{stats.count}</p>
                  <p className="text-xs text-neutral-500">Totaal beoordelingen</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-green-600">
                    {stats.distribution[5] + stats.distribution[4]}
                  </p>
                  <p className="text-xs text-neutral-500">Positief (4-5 ster)</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <ThumbsUp className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-black dark:text-white">
                    {ratings.filter(r => r.review).length}
                  </p>
                  <p className="text-xs text-neutral-500">Met toelichting</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Rating Distribution */}
          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardHeader>
              <CardTitle className="text-base">Verdeling</CardTitle>
              <CardDescription>Score verdeling over alle beoordelingen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <RatingBar stars={5} count={stats.distribution[5]} total={stats.count} />
              <RatingBar stars={4} count={stats.distribution[4]} total={stats.count} />
              <RatingBar stars={3} count={stats.distribution[3]} total={stats.count} />
              <RatingBar stars={2} count={stats.distribution[2]} total={stats.count} />
              <RatingBar stars={1} count={stats.distribution[1]} total={stats.count} />
            </CardContent>
          </Card>

          {/* Ratings List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filter */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-neutral-500">
                {filteredRatings.length} beoordeling{filteredRatings.length !== 1 ? 'en' : ''}
              </p>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-48" data-testid="rating-filter">
                  <Filter className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle beoordelingen</SelectItem>
                  <SelectItem value="with_review">Met toelichting</SelectItem>
                  <SelectItem value="5">5 sterren</SelectItem>
                  <SelectItem value="4">4 sterren</SelectItem>
                  <SelectItem value="3">3 sterren</SelectItem>
                  <SelectItem value="2">2 sterren</SelectItem>
                  <SelectItem value="1">1 ster</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ratings */}
            {filteredRatings.length > 0 ? (
              <div className="space-y-3">
                {filteredRatings.map((rating) => (
                  <Card 
                    key={rating.id} 
                    className="border-neutral-200 dark:border-neutral-800"
                    data-testid={`rating-card-${rating.id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <StarDisplay rating={rating.rating} />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                              {rating.customer_org_name || 'Anoniem'}
                            </span>
                          </div>
                          {rating.review && (
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                              "{rating.review}"
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-neutral-400 flex items-center gap-1 flex-shrink-0">
                          <Calendar className="h-3 w-3" />
                          {new Date(rating.created_at).toLocaleDateString('nl-NL')}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-neutral-200 dark:border-neutral-800">
                <CardContent className="py-12 text-center">
                  <Star className="h-12 w-12 text-neutral-300 dark:text-neutral-700 mx-auto mb-4" strokeWidth={1.5} />
                  <p className="text-neutral-500 mb-2">Nog geen beoordelingen</p>
                  <p className="text-sm text-neutral-400">
                    {filter !== 'all' 
                      ? 'Geen beoordelingen gevonden met dit filter'
                      : 'Beoordelingen van klanten verschijnen hier zodra ze worden toegevoegd.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Tips Card */}
        <Card className="border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-neutral-600 dark:text-neutral-400" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-black dark:text-white mb-1">
                  Tips voor betere beoordelingen
                </h3>
                <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                  <li>• Communiceer proactief met uw klanten over de voortgang</li>
                  <li>• Vraag actief om feedback na afronding van een project</li>
                  <li>• Reageer snel op vragen en verzoeken</li>
                  <li>• Lever kwaliteit en onderbouw uw advies met concrete resultaten</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </SupplierLayout>
  );
};

export default SupplierRatingsPage;
